import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth-context";
import type { Tenant } from "@/lib/types";

/** Estado de assinatura do tenant, lido de subscriptions (RLS por tenant). */
export interface SubscriptionState {
  status: string | null;
  plan: string | null;
  priceInterval: "month" | "year" | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  stripeCustomerId: string | null;
}

export interface TenantBilling {
  tenant: Tenant | null;
  subscription: SubscriptionState | null;
}

interface SubscriptionRow {
  status: string | null;
  plan: string | null;
  price_interval: "month" | "year" | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean | null;
  stripe_customer_id: string | null;
}

const TENANT_COLUMNS = "id, owner_user_id, name, plan, status, pro_until, created_at, config";

async function fetchTenantBilling(userId: string): Promise<TenantBilling> {
  const { data: tenant, error: tenantError } = await supabase
    .from("tenants")
    .select(TENANT_COLUMNS)
    .eq("owner_user_id", userId)
    .single();

  if (tenantError) {
    throw new Error("Não foi possível carregar os dados do tenant.");
  }

  // A assinatura e opcional: tenant Free nunca passou pelo Checkout. A linha mais
  // recente (por current_period_end) representa o estado atual de billing.
  const { data: subscriptionRows } = await supabase
    .from("subscriptions")
    .select(
      "status, plan, price_interval, current_period_end, cancel_at_period_end, stripe_customer_id",
    )
    .eq("tenant_id", tenant.id)
    .order("current_period_end", { ascending: false, nullsFirst: false })
    .limit(1);

  const subRow = (subscriptionRows?.[0] ?? null) as SubscriptionRow | null;
  const subscription: SubscriptionState | null = subRow
    ? {
        status: subRow.status,
        plan: subRow.plan,
        priceInterval: subRow.price_interval,
        currentPeriodEnd: subRow.current_period_end,
        cancelAtPeriodEnd: subRow.cancel_at_period_end ?? false,
        stripeCustomerId: subRow.stripe_customer_id,
      }
    : null;

  return { tenant: tenant as Tenant, subscription };
}

/** Carrega tenant + assinatura do usuario logado. Cache compartilhado entre a pagina
 *  de conta e a de planos: mutar o nome ou voltar do Checkout invalida a mesma chave. */
export function useTenant(): UseQueryResult<TenantBilling, Error> {
  const { user } = useAuth();

  return useQuery<TenantBilling, Error>({
    queryKey: ["tenant-billing", user?.id],
    queryFn: () => fetchTenantBilling(user!.id),
    enabled: Boolean(user?.id),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}
